import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, RefreshCw, Trash2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIAccessPage = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [instName, setInstName] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'admin';
  const isJefeTutores = user?.role === 'jefe_tutores';

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (isAdmin) {
        const instRes = await axios.get(`${API}/admin/institutions`, { headers });
        const institutions = instRes.data || [];
        if (institutions.length === 0) { setLoading(false); return; }
        const instId = searchParams.get('inst');
        const selected = (instId && institutions.find(i => i.id === instId)) || institutions[0];
        setInstName(selected.name || '');
        url = `${API}/admin/ti-access-stats/${selected.id}`;
      } else if (isJefeTutores) {
        url = `${API}/jefe-tutores/ti-access-stats`;
      } else {
        setLoading(false); return;
      }
      const res = await axios.get(url, { headers });
      setStats(res.data);
    } catch (e) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isJefeTutores, searchParams]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const resetLogs = async () => {
    if (!window.confirm('¿Reiniciar todos los registros de acceso de tutores de grupo? Esta accion no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/jefe-tutores/ti-access-logs/reset`, { headers });
      toast.success('Registros reiniciados');
      fetchStats();
    } catch (e) { toast.error('Error al reiniciar'); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
        {isAdmin && (
          <Button variant="ghost" onClick={() => { const i = searchParams.get('inst'); navigate(i ? `/dashboard/admin?inst=${i}` : '/dashboard/admin'); }} className="mb-2" data-testid="ti-access-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al panel
          </Button>
        )}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="ti-access-title">Acceso de Tutores de Grupo</h1>
            <p className="text-sm text-slate-600 mt-1">{isAdmin && instName ? instName : 'Seguimiento de accesos de profesores a sus grupos tutorados'}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchStats} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualizar
            </Button>
            {isJefeTutores && stats && (
              <Button variant="outline" onClick={resetLogs} className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Reiniciar
              </Button>
            )}
          </div>
        </div>

        {loading && !stats && (
          <div className="flex items-center justify-center h-48">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        )}

        {stats && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="p-4 text-center">
                <div className="text-3xl font-bold text-slate-700">{stats.summary.total_ti}</div>
                <div className="text-sm text-slate-500 mt-1">Total</div>
              </Card>
              <Card className="p-4 text-center bg-green-50 border-green-200">
                <div className="text-3xl font-bold text-green-600">{stats.summary.active_ti}</div>
                <div className="text-sm text-green-600 mt-1">Han accedido</div>
              </Card>
              <Card className="p-4 text-center bg-red-50 border-red-200">
                <div className="text-3xl font-bold text-red-600">{stats.summary.inactive_ti}</div>
                <div className="text-sm text-red-600 mt-1">Sin acceso</div>
              </Card>
              <Card className="p-4 text-center bg-indigo-50 border-indigo-200">
                <div className="text-3xl font-bold text-indigo-600">{stats.summary.total_accesses}</div>
                <div className="text-sm text-indigo-600 mt-1">Accesos totales</div>
              </Card>
              <Card className="p-4 text-center bg-amber-50 border-amber-200">
                <div className="text-3xl font-bold text-amber-600">{stats.summary.active_percentage}%</div>
                <div className="text-sm text-amber-600 mt-1">Activos</div>
              </Card>
            </div>

            {/* Progress */}
            <Card className="p-4">
              <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                <span>Progreso de acceso</span>
                <span className="font-semibold">{stats.summary.active_ti} de {stats.summary.total_ti} tutores de grupo</span>
              </div>
              <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-700 rounded-full" style={{ width: `${stats.summary.active_percentage}%` }} />
              </div>
            </Card>

            {/* Per Group */}
            {stats.groups.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay tutores de grupo registrados</p>
              </Card>
            ) : (
              stats.groups.map(g => (
                <Card key={g.group_id} className="overflow-hidden" data-testid={`ti-access-group-${g.group_id}`}>
                  <div className="px-5 py-3 bg-gradient-to-r from-indigo-50 to-slate-50 border-b flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-indigo-500" />
                      <span className="font-semibold text-slate-900">{g.group_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">{g.active_ti}/{g.total_ti} activos ({g.active_percentage}%)</span>
                      <span className="text-sm text-slate-500">{g.total_accesses} accesos</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 text-xs text-slate-600">
                          <th className="text-left py-3 px-5 font-semibold">Profesor</th>
                          <th className="text-left py-3 px-5 font-semibold">Correo</th>
                          <th className="text-center py-3 px-5 font-semibold">Estado</th>
                          <th className="text-center py-3 px-5 font-semibold">Accesos</th>
                          <th className="text-right py-3 px-5 font-semibold">Último acceso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.tutores.map(t => (
                          <tr key={t.id} className="border-t hover:bg-slate-50 transition-colors" data-testid={`ti-access-row-${t.id}`}>
                            <td className="py-3 px-5 font-medium text-sm text-slate-900">{t.name}</td>
                            <td className="py-3 px-5 text-sm text-slate-500">{t.email}</td>
                            <td className="py-3 px-5 text-center">
                              {t.has_accessed ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Activo</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">Sin acceso</span>
                              )}
                            </td>
                            <td className="py-3 px-5 text-center">
                              <span className="text-lg font-bold text-slate-700">{t.access_count}</span>
                            </td>
                            <td className="py-3 px-5 text-right text-sm text-slate-500">
                              {t.last_access ? new Date(t.last_access).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Nunca'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {!stats && !loading && (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos</p>
            <Button variant="outline" onClick={fetchStats} className="mt-3">Reintentar</Button>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default TIAccessPage;
